import type { StaticEntityType, StoryType } from "@config";
import { apiClient } from "@lib/axios";
import type {
  GetPagesCountParams,
  SearchPagesParams,
  TImportedPage,
  TPage,
} from "./types";
import { filterEmptyParams } from "./utils";

const path = (path: string) => `/pages${path}`;

export const pagesApi = {
  searchPages: async (params: SearchPagesParams) => {
    return apiClient.post<TPage[]>(path("/search"), filterEmptyParams(params));
  },

  /**
   * `recordView` puts the page on the caller's Recently viewed. Opt-in, because
   * this fetch is not always someone opening the page: loading a version fetches
   * its story too, and a duplicate reads its source through here. Recording on
   * every fetch put stories on the dashboard that nobody had opened.
   */
  getPageById: async (
    id: string,
    options?: { recordView?: boolean; versionId?: string },
  ) => {
    return apiClient.get<TPage>(path(`/${id}`), {
      params: {
        ...(options?.recordView ? { recordView: true } : {}),
        // Send flow: someone invited to a version only reads its story through it.
        ...(options?.versionId ? { versionId: options.versionId } : {}),
      },
    });
  },

  getRecentPages: async (folders: string[]) => {
    return apiClient.get<TPage[]>(path("/recent"), {
      params: { folders },
    });
  },

  getImportedPages: async (limit = 5) => {
    return apiClient.get<TImportedPage[]>(path("/imported"), {
      params: { limit },
    });
  },

  getPageSlides: async (id: string, count: number | null = null) => {
    return apiClient.get<StaticEntityType[]>(path(`/${id}/slides`), {
      params: count ? { count } : {},
    });
  },

  getUploadFileToken: async (
    filename: string,
    contentType: string,
    typeStory: boolean = false,
  ) => {
    return apiClient.post<{
      url: { fields: Record<string, string>; key: string; url: string };
    }>(path("/uploadFileToken"), {
      filename,
      contentType,
      typeStory,
    });
  },

  deletePage: async (id: string) => {
    return apiClient.delete<TPage>(path(`/${id}`));
  },

  updatePage: async (id: string, data: Partial<TPage>) => {
    return apiClient.post<TPage>(path(`/v2/${id}`), data);
  },

  getApiToken: async (id: string) => {
    return apiClient.get<{ token: string; iat: number }>(path(`/${id}/token`));
  },

  getCollaborationRoomTitles: async (ids: string[]) => {
    return apiClient.get<{ _id: string; title: string; room: string }[]>(
      path("/resolve-collaboration-titles"),
      {
        params: { ids: ids.join(",") },
      },
    );
  },

  getStoryTitleCount: async ({ title, id }: { title: string; id: string }) => {
    return apiClient.get<{ count: number }>(path("/validate-title"), {
      params: { title, id },
    });
  },

  getPagesCount: async (params: GetPagesCountParams) => {
    return apiClient.get<{ count: number }>(path("/count"), {
      params: filterEmptyParams(params),
    });
  },

  getSessionMetadata: async (pathParam: string) => {
    return apiClient.get<string[]>(path("/session-metadata"), {
      params: { path: pathParam },
    });
  },

  validatePageContent: async (id: string) => {
    return apiClient.get(path(`/validate-content/${id}`));
  },

  getPagesWithStoryBrain: async (currentStoryId: string) => {
    return apiClient.get<StoryType[]>(path("/with-story-brain"), {
      params: { id: currentStoryId },
    });
  },

  updatePageShareTime: async (id: string) => {
    return apiClient.post<void>(path(`/${id}/shared-at`));
  },

  createPage: async (story: StoryType) => {
    return apiClient.post<StoryType>(path("/v2"), story);
  },

  validateIframeUrl: async (url: string) => {
    return apiClient.post<{
      embeddable: boolean;
      reason: string;
      message: string;
    }>(path("/validate-iframe-url"), { url });
  },

  saveAndPublishPage: async (id: string, data: Partial<TPage>) => {
    return apiClient.post<StoryType>(path(`/v2/save-and-publish/${id}`), data);
  },

  getAccessibleLivePages: async (folderId?: string) => {
    // Returns Live pages accessible to the user, including title in settings.
    // hostUserId marks the hidden page a user's standalone versions hang off.
    return apiClient.get<
      {
        _id: string;
        settings: { title: string };
        folderId: string;
        hostUserId?: string | null;
      }[]
    >(path("/accessible-live-pages"), {
      params: folderId ? { folderId } : {},
    });
  },

  /**
   * The caller's host page for standalone versions, made on first use. Open
   * to every signed-in role: the point is a version with no story of the
   * user's own behind it. See features/standalone-versions.
   */
  getOrCreateHostPage: async () => {
    return apiClient.post<TPage>(path("/v2/host-page"));
  },
};
